import { Rights } from '@dokucoach/common-types';
import { IInvoiceSearchTypeDTO } from '@dokucoach/invoicing-types';
import { useContextAuthManager } from 'components/AuthManager';
import { BaseDataList } from 'constants/base-data-list';
import MainMenu from 'constants/main-menu';
import { NursingTaskCategory } from 'constants/nursing-task-category';
import routePaths from 'constants/route-paths';
import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useClickAway } from 'react-use';
import { DoctorListData } from 'services/doctors';
import { EmployeeListData } from 'services/employees';
import { InsuranceListData } from 'services/insurances';
import { ResidentGroupPropType, ResidentPropType } from 'services/residentGroups';
import { WelfareOfficeListData } from 'services/welfareOffices';
import { getRoute } from 'utils/path';

export type InvoicingOverviewTimeFilter = {
  year: number;
  month: number;
};

/** What the invoice overview showed when an invoice was opened from it */
export type InvoicingOverviewView = InvoicingOverviewTimeFilter & {
  residentId: string;
  searchFilter: IInvoiceSearchTypeDTO | undefined;
  // Keys of the resident and invoice groups that were open in the table
  expandedGroupKeys: unknown[][];
};

/** The list an invoice was opened from, the back button of the invoice leads there */
export type InvoiceOrigin = {
  pathname: string;
  // E.g. the execution plan after an invoice generation on the invoice overview
  search: string;
  // The invoice shown last, after "Bearbeiten" it is the new draft
  invoiceId: string;
  // Only the invoice overview restores its filters and open rows
  invoicingOverviewView?: InvoicingOverviewView;
};

type ContextType = {
  menuActive: string;
  setMenuActive: Dispatch<SetStateAction<string>>;
  refMenu: any;
  asideSelected: MainMenu;
  setAsideSelected: Dispatch<SetStateAction<MainMenu>>;
  baseDataListSelected: BaseDataList;
  setBaseDataListSelected: (data: BaseDataList) => void;
  isEditMode: boolean;
  setIsEditMode: (isEditMode: boolean) => void;
  residentGroups: ResidentGroupPropType[];
  setResidentGroups: (data: ResidentGroupPropType[]) => void;
  employees: EmployeeListData[];
  setEmployees: (data: EmployeeListData[]) => void;
  filteredEmployees: EmployeeListData[];
  setFilteredEmployees: (data: EmployeeListData[]) => void;
  residents: ResidentPropType[];
  setResidents: (data: ResidentPropType[]) => void;
  filteredResidents: ResidentPropType[];
  setFilteredResidents: (data: ResidentPropType[]) => void;
  welfareOffices: WelfareOfficeListData[];
  setWelfareOffices: (data: WelfareOfficeListData[]) => void;
  filteredWelfareOffices: WelfareOfficeListData[];
  setFilteredWelfareOffices: (data: WelfareOfficeListData[]) => void;
  doctors: DoctorListData[];
  setDoctors: (data: DoctorListData[]) => void;
  filteredDoctors: DoctorListData[];
  setFilteredDoctors: (data: DoctorListData[]) => void;
  insurances: InsuranceListData[];
  setInsurances: (data: InsuranceListData[]) => void;
  filteredInsurances: InsuranceListData[];
  setFilteredInsurances: (data: InsuranceListData[]) => void;
  resetAllFilteredLists: () => void;
  shouldRefetch: string | undefined;
  setShouldRefetch: (id: string | undefined) => void;
  shouldRefetchGroups: string | undefined;
  setShouldRefetchGroups: (id: string | undefined) => void;
  shouldRefetchHeaderFlags: string | undefined;
  setShouldRefetchHeaderFlags: (id: string | undefined) => void;
  nursingTaskCategory: NursingTaskCategory;
  setNursingTaskCategory: (category: NursingTaskCategory) => void;
  invoicingOverviewTimeFilter: InvoicingOverviewTimeFilter | undefined;
  setInvoicingOverviewTimeFilter: (filter: InvoicingOverviewTimeFilter) => void;
  invoiceOrigin: InvoiceOrigin | undefined;
  setInvoiceOrigin: Dispatch<SetStateAction<InvoiceOrigin | undefined>>;
};

type Prop = {
  children: ReactNode;
  setEditMode: (isEditMode: boolean) => void;
};

const Context = createContext({} as ContextType);

const LayoutContext = ({ children, setEditMode }: Prop) => {
  const { user, hasPermission } = useContextAuthManager();
  const [menuActive, setMenuActive] = useState('');
  const [asideSelected, setAsideSelected] = useState<MainMenu>(MainMenu.ANALYTICS);
  const [baseDataListSelected, setBaseDataListSelected] = useState<BaseDataList>(
    BaseDataList.RESIDENTS,
  );
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [residentGroups, setResidentGroups] = useState<ResidentGroupPropType[]>([]);
  const [employees, setEmployees] = useState<EmployeeListData[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<EmployeeListData[]>([]);
  const [residents, setResidents] = useState<ResidentPropType[]>([]);
  const [filteredResidents, setFilteredResidents] = useState<ResidentPropType[]>([]);
  const [doctors, setDoctors] = useState<DoctorListData[]>([]);
  const [filteredDoctors, setFilteredDoctors] = useState<DoctorListData[]>([]);
  const [insurances, setInsurances] = useState<InsuranceListData[]>([]);
  const [filteredInsurances, setFilteredInsurances] = useState<InsuranceListData[]>([]);
  const [welfareOffices, setWelfareOffices] = useState<WelfareOfficeListData[]>([]);
  const [filteredWelfareOffices, setFilteredWelfareOffices] = useState<WelfareOfficeListData[]>([]);
  const [shouldRefetch, setShouldRefetch] = useState<string | undefined>(undefined);
  const [shouldRefetchGroups, setShouldRefetchGroups] = useState<string | undefined>(undefined);
  const [shouldRefetchHeaderFlags, setShouldRefetchHeaderFlags] = useState<string | undefined>(
    undefined,
  );
  // Kept here instead of the dashboard, so the selection survives switching between residents and groups.
  // Logout reloads the page, so every login starts with ALL again.
  const [nursingTaskCategory, setNursingTaskCategory] = useState<NursingTaskCategory>(
    NursingTaskCategory.ALL,
  );
  // Year and month the user picked on the invoice overview, kept until the next login (logout reloads the page).
  // Undefined until the user changes the filter, so the overview starts with the current month.
  const [invoicingOverviewTimeFilter, setInvoicingOverviewTimeFilter] =
    useState<InvoicingOverviewTimeFilter>();
  const [invoiceOrigin, setInvoiceOrigin] = useState<InvoiceOrigin>();
  const location = useLocation();

  // TODO: Add permissions for sub menus to only select Aside Menu if user has permission
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith(routePaths.DASHBOARD)) {
      setAsideSelected(MainMenu.DASHBOARD);
    } else if (path.startsWith(routePaths.BASE_DATA)) {
      setAsideSelected(MainMenu.BASE_DATA);
    } else if (path.startsWith(routePaths.CONFIGURATION)) {
      setAsideSelected(MainMenu.CONFIGURATION);
    } else if (path.startsWith(routePaths.LISTS)) {
      setAsideSelected(MainMenu.ANALYTICS);
    } else if (path.startsWith(routePaths.INDICATORS)) {
      setAsideSelected(MainMenu.INDICATORS);
    } else if (path.startsWith(routePaths.INVOICING)) {
      setAsideSelected(MainMenu.INVOICING);
    } else {
      // If the path (current URL) does not include any of the above routes, that means we are on the home page,
      // probably after a login. If this is the case, set the selected aside menu according to the user.
      // If the user can access the CARE module, show the care documentation / dashboard aside menu, if not,
      // show the base data menu.
      setAsideSelected(hasPermission(Rights.CARE.READ) ? MainMenu.DASHBOARD : MainMenu.BASE_DATA);
    }
    if (path.includes('employee')) {
      setBaseDataListSelected(BaseDataList.EMPLOYEES);
    } else if (path.includes('resident')) {
      setBaseDataListSelected(BaseDataList.RESIDENTS);
    } else if (path.includes('doctor')) {
      setBaseDataListSelected(BaseDataList.DOCTORS);
    }
  }, [user, location]);

  useEffect(() => {
    setIsEditMode(false);
  }, [location.pathname]);

  // The list of an invoice is only remembered on the way from the invoice back to that list
  useEffect(() => {
    const isInvoicePage = !!matchPath(location.pathname, {
      path: getRoute([routePaths.INVOICING, routePaths.INVOICE]),
      exact: true,
    });
    setInvoiceOrigin((origin) =>
      isInvoicePage || origin?.pathname === location.pathname ? origin : undefined,
    );
  }, [location.pathname]);

  useEffect(() => {
    setEditMode(isEditMode);
  }, [isEditMode]);

  const refMenu = useRef(null);

  const resetAllFilteredLists = () => {
    setFilteredEmployees([...employees]);
    setFilteredResidents([...residents]);
    setFilteredDoctors([...doctors]);
    setFilteredInsurances([...insurances]);
  };

  useClickAway(
    refMenu,
    () => {
      setMenuActive('');
    },
    ['click'],
  );

  return (
    <Context.Provider
      value={{
        menuActive,
        setMenuActive,
        refMenu,
        asideSelected,
        setAsideSelected,
        baseDataListSelected,
        setBaseDataListSelected,
        isEditMode,
        setIsEditMode,
        residentGroups,
        setResidentGroups,
        employees,
        setEmployees,
        filteredEmployees,
        setFilteredEmployees,
        residents,
        setResidents,
        filteredResidents,
        setFilteredResidents,
        doctors,
        setDoctors,
        filteredDoctors,
        setFilteredDoctors,
        insurances,
        setInsurances,
        filteredInsurances,
        setFilteredInsurances,
        welfareOffices,
        setWelfareOffices,
        filteredWelfareOffices,
        setFilteredWelfareOffices,
        resetAllFilteredLists,
        shouldRefetch,
        setShouldRefetch,
        shouldRefetchGroups,
        setShouldRefetchGroups,
        shouldRefetchHeaderFlags,
        setShouldRefetchHeaderFlags,
        nursingTaskCategory,
        setNursingTaskCategory,
        invoicingOverviewTimeFilter,
        setInvoicingOverviewTimeFilter,
        invoiceOrigin,
        setInvoiceOrigin,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const useContextLayout = () => {
  return useContext(Context);
};

export default LayoutContext;
